var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import Editor from '@draft-js-plugins/editor';
import createMentionPlugin, { defaultSuggestionsFilter } from '@draft-js-plugins/mention';
import { EditorState, getDefaultKeyBinding, Modifier, RichUtils, ContentState, convertToRaw, convertFromRaw, } from 'draft-js';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import ReactDOM from 'react-dom';
export function MentionInput(_a) {
    var style = _a.style, placeholder = _a.placeholder, mentionData = _a.mentionData, onDone = _a.onDone, onEscape = _a.onEscape, _b = _a.autoFocus, autoFocus = _b === void 0 ? false : _b, footer = _a.footer, _c = _a.disabled, disabled = _c === void 0 ? false : _c;
    var ref = useRef(null);
    var containerRef = useRef(null);
    var _d = __read(useState(function () { return EditorState.createEmpty(); }), 2), editorState = _d[0], setEditorState = _d[1];
    var _e = __read(useState(false), 2), open = _e[0], setOpen = _e[1];
    var _f = __read(useState(mentionData), 2), suggestions = _f[0], setSuggestions = _f[1];
    useEffect(function () {
        if (autoFocus) {
            setTimeout(function () {
                var _a;
                (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 10);
        }
    }, [autoFocus]);
    var _g = useMemo(function () {
        var mentionPlugin = createMentionPlugin({
            theme: {
                mention: 'mention',
                mentionSuggestionsPopup: 'mentionSuggestionsPopup',
                mentionSuggestions: 'mentionSuggestions',
                mentionSuggestionsEntryFocused: 'mentionSuggestionsEntryFocused',
                mentionSuggestionsEntry: 'mentionSuggestionsEntry',
            },
            mentionPrefix: '@',
            supportWhitespace: true,
        });
        var MentionSuggestions = mentionPlugin.MentionSuggestions;
        var plugins = [mentionPlugin];
        return { plugins: plugins, MentionSuggestions: MentionSuggestions };
    }, []), MentionSuggestions = _g.MentionSuggestions, plugins = _g.plugins;
    var onOpenChange = function (_open) {
        setOpen(_open);
    };
    var onSearchChange = useCallback(function (_a) {
        var value = _a.value;
        var filtered = mentionData;
        if (value !== '') {
            filtered = defaultSuggestionsFilter(value, mentionData);
        }
        setSuggestions(filtered);
    }, [mentionData]);
    function removeSelectedBlocksStyle(editorState) {
        var newContentState = RichUtils.tryToRemoveBlockStyle(editorState);
        if (newContentState) {
            return EditorState.push(editorState, newContentState, 'change-block-type');
        }
        return editorState;
    }
    function getResetEditorState(editorState) {
        var blocks = editorState.getCurrentContent().getBlockMap().toList();
        var updatedSelection = editorState.getSelection().merge({
            anchorKey: blocks.first().get('key'),
            anchorOffset: 0,
            focusKey: blocks.last().get('key'),
            focusOffset: blocks.last().getLength(),
        });
        var newContentState = Modifier.removeRange(editorState.getCurrentContent(), updatedSelection, 'forward');
        var newState = EditorState.push(editorState, newContentState, 'remove-range');
        return removeSelectedBlocksStyle(newState);
    }
    function handleKeyCommand(command, editorState, eventTimeStamp) {
        if (command === 'new-line') {
            var currentContent = editorState.getCurrentContent();
            var selectionState = editorState.getSelection();
            var newContent = Modifier.insertText(currentContent, selectionState, '\n');
            var newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
            setEditorState(newEditorState);
            return 'handled';
        }
        if (command === 'esc') {
            ref.current.blur();
            onEscape === null || onEscape === void 0 ? void 0 : onEscape();
            return 'handled';
        }
        return 'not-handled';
    }
    function bindKey(e) {
        if (e.shiftKey && e.key === 'Enter') {
            return 'new-line';
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            return undefined;
        }
        if (e.key === 'Escape') {
            return open ? undefined : 'esc';
        }
        if (e.key === 'Tab' && open) {
            return undefined;
        }
        return getDefaultKeyBinding(e);
    }
    function handleReturn(e, editorState) {
        if (!open && !e.shiftKey) {
            var currentContent = editorState.getCurrentContent();
            var rawContent = convertToRaw(currentContent);
            var text = currentContent.getPlainText();
            var entities = rawContent.entityMap;
            var mentionedUserIds = [];
            var mentionedGroupIds = [];
            for (var key in entities) {
                if (Object.prototype.hasOwnProperty.call(entities, key)) {
                    var mentionData_1 = entities[key].data;
                    if (mentionData_1.mention.isGroup) {
                        mentionedGroupIds.push(mentionData_1.mention.itemId);
                    }
                    else {
                        mentionedUserIds.push(mentionData_1.mention.itemId);
                    }
                }
            }
            onDone === null || onDone === void 0 ? void 0 : onDone(mentionedUserIds, mentionedGroupIds, rawContent, text);
            var clearState = getResetEditorState(editorState);
            setEditorState(clearState);
            return 'handled';
        }
        return 'not-handled';
    }
    return (React.createElement("div", { ref: containerRef, style: style, onClick: function (e) {
            var _a;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.isSameNode(e.target)) {
                ref.current.focus();
            }
        } },
        React.createElement(Editor, { readOnly: disabled, placeholder: placeholder, editorState: editorState, onChange: setEditorState, handleKeyCommand: handleKeyCommand, handleReturn: handleReturn, keyBindingFn: bindKey, plugins: plugins, ref: ref }),
        ReactDOM.createPortal(React.createElement(MentionSuggestions, { open: open, onOpenChange: onOpenChange, suggestions: suggestions, onSearchChange: onSearchChange }), document.getElementById('portal')),
        !!footer && footer));
}
function getEditorState(rawContent) {
    try {
        var draftContent = JSON.parse(rawContent);
        var content = convertFromRaw(draftContent);
        var editorState = EditorState.createWithContent(content);
        return editorState;
    }
    catch (error) {
        console.error(error);
        return EditorState.createWithContent(ContentState.createFromText('Content of message is corrupt...'));
    }
}
export function Mentions(_a) {
    var style = _a.style, rawContent = _a.rawContent, footer = _a.footer;
    var _b = __read(useState(function () { return getEditorState(rawContent); }), 2), editorState = _b[0], setEditorState = _b[1];
    var plugins = useMemo(function () {
        var mentionPlugin = createMentionPlugin({
            theme: {
                mention: 'mention',
            },
            mentionPrefix: '@',
            supportWhitespace: true,
        });
        var plugins = [mentionPlugin];
        return plugins;
    }, []);
    return (React.createElement("div", { style: style },
        React.createElement(Editor, { readOnly: true, onChange: setEditorState, editorState: editorState, plugins: plugins }),
        !!footer && footer));
}
